import { useState } from "react";
import { FiCheckCircle, FiCreditCard, FiEdit2, FiSmartphone } from "react-icons/fi";
import MerchantBankSetupForm from "./MerchantBankSetupForm";
import MerchantMobileMoneySetupForm from "./MerchantMobileMoneySetupForm";

const maskAccount = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length < 4) return digits;
  return `····${digits.slice(-4)}`;
};

const hasVerifiedBank = (merchant) =>
  Boolean(
    merchant?.payoutVerified &&
      merchant?.payoutMethod === "bank_transfer" &&
      merchant?.bankAccountNumber
  );

const hasMobilePayout = (merchant) =>
  Boolean(
    merchant?.payoutMethod === "mobile_money" &&
      merchant?.mobileMoneyProvider &&
      merchant?.mobileMoneyNumber
  );

const MerchantPayoutDestination = ({ merchant, isLoading }) => {
  const [modal, setModal] = useState(null);
  const [setupMethod, setSetupMethod] = useState("bank");

  const bankActive = hasVerifiedBank(merchant);
  const mobileActive = hasMobilePayout(merchant);
  const payoutReady = bankActive || mobileActive;

  const closeModal = () => setModal(null);

  const handleSaved = () => {
    closeModal();
    setSetupMethod("bank");
  };

  if (isLoading) {
    return (
      <div className="dashboard-card">
        <div className="dashboard-payout-skeleton" aria-hidden="true" />
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-card">
        <div className="dashboard-payout-destination__head">
          <div>
            <h2 className="dashboard-card__title">Payout destination</h2>
            <p className="dashboard-form-hint dashboard-form-hint--flush">
              {payoutReady
                ? "Your earnings are sent to this account when you request a payout."
                : "Add where you want to receive withdrawals. Nothing is verified until you save."}
            </p>
          </div>
          {payoutReady && (
            <button
              type="button"
              className="dashboard-btn dashboard-btn--secondary"
              onClick={() => setModal(bankActive ? "view-bank" : "view-mobile")}
            >
              <FiEdit2 aria-hidden="true" /> Manage
            </button>
          )}
        </div>

        {payoutReady ? (
          <div className="dashboard-payout-summary">
            <div className="dashboard-payout-summary__icon" aria-hidden="true">
              {bankActive ? <FiCreditCard /> : <FiSmartphone />}
            </div>
            <div className="dashboard-payout-summary__body">
              <div className="dashboard-payout-summary__top">
                <span className="dashboard-payout-summary__type">
                  {bankActive ? "Bank transfer" : "Mobile money"}
                </span>
                <span className="dashboard-payout-summary__badge">
                  <FiCheckCircle aria-hidden="true" /> Active
                </span>
              </div>
              {bankActive ? (
                <>
                  <p className="dashboard-payout-summary__primary">{merchant.bankName}</p>
                  <p className="dashboard-payout-summary__secondary">{merchant.bankAccountName}</p>
                  <p className="dashboard-payout-summary__meta">
                    Account {maskAccount(merchant.bankAccountNumber)}
                  </p>
                </>
              ) : (
                <>
                  <p className="dashboard-payout-summary__primary">{merchant.mobileMoneyProvider}</p>
                  <p className="dashboard-payout-summary__meta">{merchant.mobileMoneyNumber}</p>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="dashboard-payout-setup">
            <div className="dashboard-payout-method-tabs" role="tablist" aria-label="Payout method">
              <button
                type="button"
                role="tab"
                aria-selected={setupMethod === "bank"}
                className={setupMethod === "bank" ? "active" : ""}
                onClick={() => setSetupMethod("bank")}
              >
                <FiCreditCard aria-hidden="true" /> Bank transfer
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={setupMethod === "mobile"}
                className={setupMethod === "mobile" ? "active" : ""}
                onClick={() => setSetupMethod("mobile")}
              >
                <FiSmartphone aria-hidden="true" /> Mobile money
              </button>
            </div>

            {setupMethod === "bank" ? (
              <MerchantBankSetupForm merchant={merchant} onSuccess={handleSaved} />
            ) : (
              <MerchantMobileMoneySetupForm merchant={merchant} onSuccess={handleSaved} />
            )}
          </div>
        )}
      </div>

      {modal && (
        <div className="dashboard-modal-backdrop" onClick={closeModal} role="presentation">
          <div
            className="dashboard-modal dashboard-payout-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="payout-modal-title"
          >
            {modal === "view-bank" && bankActive && (
              <>
                <h3 id="payout-modal-title">Bank account</h3>
                <div className="dashboard-payout-modal__details">
                  <div className="dashboard-payout-modal__row">
                    <span>Status</span>
                    <strong className="dashboard-payout-modal__verified">Verified</strong>
                  </div>
                  <div className="dashboard-payout-modal__row">
                    <span>Bank</span>
                    <strong>{merchant.bankName}</strong>
                  </div>
                  <div className="dashboard-payout-modal__row">
                    <span>Account name</span>
                    <strong>{merchant.bankAccountName}</strong>
                  </div>
                  <div className="dashboard-payout-modal__row">
                    <span>Account number</span>
                    <strong>{merchant.bankAccountNumber}</strong>
                  </div>
                </div>
                <div className="dashboard-modal__actions dashboard-payout-modal__actions">
                  <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={closeModal}>
                    Close
                  </button>
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn--primary"
                    onClick={() => setModal("edit-bank")}
                  >
                    Change bank account
                  </button>
                </div>
                <button
                  type="button"
                  className="dashboard-payout-modal__switch"
                  onClick={() => setModal("edit-mobile")}
                >
                  Switch to mobile money instead
                </button>
              </>
            )}

            {modal === "view-mobile" && mobileActive && (
              <>
                <h3 id="payout-modal-title">Mobile money</h3>
                <div className="dashboard-payout-modal__details">
                  <div className="dashboard-payout-modal__row">
                    <span>Provider</span>
                    <strong>{merchant.mobileMoneyProvider}</strong>
                  </div>
                  <div className="dashboard-payout-modal__row">
                    <span>Number</span>
                    <strong>{merchant.mobileMoneyNumber}</strong>
                  </div>
                </div>
                <div className="dashboard-modal__actions dashboard-payout-modal__actions">
                  <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={closeModal}>
                    Close
                  </button>
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn--primary"
                    onClick={() => setModal("edit-mobile")}
                  >
                    Update details
                  </button>
                </div>
                <button
                  type="button"
                  className="dashboard-payout-modal__switch"
                  onClick={() => setModal("edit-bank")}
                >
                  Switch to bank transfer instead
                </button>
              </>
            )}

            {modal === "edit-bank" && (
              <>
                <h3 id="payout-modal-title">
                  {bankActive ? "Change bank account" : "Set up bank transfer"}
                </h3>
                <MerchantBankSetupForm
                  merchant={merchant}
                  onSuccess={handleSaved}
                  onCancel={bankActive ? () => setModal("view-bank") : closeModal}
                />
              </>
            )}

            {modal === "edit-mobile" && (
              <>
                <h3 id="payout-modal-title">
                  {mobileActive ? "Update mobile money" : "Set up mobile money"}
                </h3>
                <MerchantMobileMoneySetupForm
                  merchant={merchant}
                  onSuccess={handleSaved}
                  onCancel={mobileActive ? () => setModal("view-mobile") : closeModal}
                />
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MerchantPayoutDestination;
